import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout.jsx';
import ItemCard from '../components/ItemCard.jsx';
import { useList } from '../hooks/useLists.js';
import client from '../api/client.js';

const styles = {
  tabsRow: {
    display: 'flex',
    overflowX: 'auto',
    padding: '10px 12px',
    gap: '8px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    backgroundColor: 'transparent',
    flexShrink: 0,
  },
  tab: {
    whiteSpace: 'nowrap',
    padding: '7px 14px',
    borderRadius: '20px',
    border: 'none',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    backgroundColor: '#FFFFFF',
    color: '#2D2424',
    flexShrink: 0,
  },
  tabActive: {
    background: 'linear-gradient(135deg, #F05A35 0%, #F4956A 100%)',
    color: '#FFFFFF',
  },
  tabArchive: {
    backgroundColor: '#FFFFFF',
    color: '#C4A99A',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 12px 12px',
  },
  itemsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
  },
  fab: {
    position: 'fixed',
    bottom: '24px',
    right: '16px',
    background: 'linear-gradient(135deg, #F05A35 0%, #F4956A 100%)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '28px',
    padding: '14px 20px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(240,90,53,0.4)',
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
  },
  searchInput: {
    width: '100%',
    padding: '9px 14px 9px 36px',
    borderRadius: '20px',
    border: '1px solid #F4956A',
    fontSize: '14px',
    backgroundColor: '#FFFFFF',
    color: '#2D2424',
    outline: 'none',
    boxSizing: 'border-box',
  },
  searchWrap: {
    padding: '0 12px 10px',
    position: 'relative',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 16px',
    color: 'var(--tg-theme-hint-color, #8e8e93)',
  },
  emptyIcon: {
    fontSize: '40px',
    marginBottom: '8px',
  },
  loadingText: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--tg-theme-hint-color, #8e8e93)',
  },
};

export default function ListDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { list, loading, error, refetch } = useList(id);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [archiveView, setArchiveView] = useState(false);
  const [archivedItems, setArchivedItems] = useState([]);
  const [search, setSearch] = useState('');
  const [addCategoryModal, setAddCategoryModal] = useState(false);
  const [newCategoryTitle, setNewCategoryTitle] = useState('');
  const [renameCategoryModal, setRenameCategoryModal] = useState(null);
  const [renameCategoryTitle, setRenameCategoryTitle] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');

  const fetchItems = useCallback(async (categoryId) => {
    if (!categoryId) return;
    setItemsLoading(true);
    try {
      const response = await client.get(`/items?category_id=${categoryId}`);
      setItems(response.data);
    } catch (err) {
      console.error('Failed to fetch items:', err);
    } finally {
      setItemsLoading(false);
    }
  }, []);

  // Auto-select category when list loads (restore from URL or pick first)
  useEffect(() => {
    if (list?.categories?.length > 0 && !selectedCategory) {
      const returnCategoryId = searchParams.get('category_id');
      const sorted = [...list.categories].sort((a, b) => a.position - b.position);
      const target = returnCategoryId
        ? sorted.find((c) => c.id === returnCategoryId) || sorted[0]
        : sorted[0];
      setSelectedCategory(target);
    }
  }, [list]);

  useEffect(() => {
    if (selectedCategory) {
      fetchItems(selectedCategory.id);
    }
  }, [selectedCategory, fetchItems]);

  const handleCategorySelect = (category) => {
    setArchiveView(false);
    setSelectedCategory(category);
    setSearch('');
  };

  const handleArchiveView = async () => {
    setSelectedCategory(null);
    setArchiveView(true);
    setItemsLoading(true);
    try {
      const res = await client.get(`/items?list_id=${id}&include_archived=true`);
      setArchivedItems(res.data.filter((i) => i.is_archived));
    } catch {
      setArchivedItems([]);
    } finally {
      setItemsLoading(false);
    }
  };

  // Can user add items to a specific category?
  const canEditCategory = (categoryId) => {
    if (!list) return false;
    if (list.my_role === 'admin') return true;
    const memberships = list.my_memberships || [];
    return memberships.some(
      (m) => (m.role === 'edit' || m.role === 'admin') && (!m.category_id || m.category_id === categoryId)
    );
  };

  const handleArchive = async (itemId) => {
    try {
      await client.post(`/items/${itemId}/archive`);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
    } catch (err) {
      alert('Не удалось архивировать');
    }
  };

  const handleUnarchive = async (itemId) => {
    try {
      await client.post(`/items/${itemId}/unarchive`);
      if (archiveView) {
        setArchivedItems((prev) => prev.filter((i) => i.id !== itemId));
      } else {
        fetchItems(selectedCategory?.id);
      }
    } catch (err) {
      alert('Не удалось восстановить');
    }
  };

  const handleAddCategory = () => {
    setNewCategoryTitle('');
    setAddCategoryModal(true);
  };

  const handleAddCategorySubmit = async () => {
    if (!newCategoryTitle.trim()) return;
    try {
      const response = await client.post('/categories', {
        list_id: id,
        title: newCategoryTitle.trim(),
        position: (list?.categories?.length || 0),
      });
      setAddCategoryModal(false);
      refetch();
      setSelectedCategory(response.data);
    } catch (err) {
      alert('Не удалось создать категорию');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!confirm(`Удалить категорию "${category.title}"? Все товары внутри тоже будут удалены.`)) return;
    try {
      await client.delete(`/categories/${category.id}`);
      if (selectedCategory?.id === category.id) {
        setSelectedCategory(null);
        setItems([]);
      }
      refetch();
    } catch {
      alert('Не удалось удалить категорию');
    }
  };

  const handleRenameCategorySubmit = async () => {
    if (!renameCategoryTitle.trim() || !renameCategoryModal) return;
    try {
      await client.patch(`/categories/${renameCategoryModal.id}`, { title: renameCategoryTitle.trim() });
      setRenameCategoryModal(null);
      refetch();
    } catch {
      alert('Не удалось переименовать категорию');
    }
  };

  const handleSaveTitle = async () => {
    setEditingTitle(false);
    if (!titleValue.trim() || titleValue.trim() === list.title) return;
    try {
      await client.patch(`/lists/${id}`, { title: titleValue.trim() });
      refetch();
    } catch {}
  };

  const [shareModal, setShareModal] = useState(false);
  const [shareRole, setShareRole] = useState('view');
  const [shareScope, setShareScope] = useState('list');
  const [shareLink, setShareLink] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedInviteId, setCopiedInviteId] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [shareModalLoading, setShareModalLoading] = useState(false);

  const loadShareData = async () => {
    setShareModalLoading(true);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        client.get(`/lists/${id}/members`),
        client.get(`/lists/${id}/invites`),
      ]);
      setMembers(membersRes.data);
      setInvites(invitesRes.data);
    } catch {
      // ignore
    } finally {
      setShareModalLoading(false);
    }
  };

  const handleOpenShareModal = () => {
    setShareModal(true);
    setShareLink('');
    setCopied(false);
    setShareScope('list');
    loadShareData();
  };

  const handleGenerateLink = async () => {
    setShareLoading(true);
    try {
      const res = await client.post(`/lists/${id}/invite`, {
        role: shareRole,
        category_id: shareScope === 'category' && selectedCategory ? selectedCategory.id : null,
      });
      setShareLink(res.data.invite_url);
      setInvites((prev) => [res.data, ...prev]);
    } catch {
      alert('Не удалось создать ссылку');
    } finally {
      setShareLoading(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await client.delete(`/lists/${id}/invites/${inviteId}`);
      setInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
      if (invites.find((inv) => inv.id === inviteId)?.invite_url === shareLink) {
        setShareLink('');
      }
    } catch {
      alert('Не удалось отозвать ссылку');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!confirm('Удалить участника из вишлиста?')) return;
    try {
      await client.delete(`/lists/${id}/members/${memberId}`);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch {
      alert('Не удалось удалить участника');
    }
  };

  const handleCopy = () => {
    const doCopy = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareLink).then(doCopy).catch(() => {
        const ta = document.createElement('textarea');
        ta.value = shareLink;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        doCopy();
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = shareLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      doCopy();
    }
  };

  const handleLeave = async () => {
    if (!confirm('Покинуть этот вишлист? Вы потеряете доступ.')) return;
    try {
      await client.delete(`/lists/${id}/leave`);
      navigate('/');
    } catch (err) {
      alert(err?.response?.data?.error || 'Не удалось покинуть вишлист');
    }
  };

  const btnStyle = { background: 'none', border: 'none', cursor: 'pointer', padding: '8px', fontSize: '18px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' };

  const rightAction = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <button
        onClick={() => { refetch(); if (selectedCategory) fetchItems(selectedCategory.id); }}
        style={{ ...btnStyle, color: 'var(--tg-theme-hint-color, #8e8e93)' }}
        title="Обновить"
      >
        ↻
      </button>
      <button
        onClick={handleOpenShareModal}
        style={{ ...btnStyle, color: 'var(--tg-theme-link-color, #007aff)' }}
        title="Поделиться"
      >
        🔗
      </button>
      {list && list.my_role !== 'admin' && (
        <button
          onClick={handleLeave}
          style={{ ...btnStyle, color: '#ff3b30' }}
          title="Покинуть"
        >
          🚪
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <Layout title="Загрузка..." showBack>
        <div style={styles.loadingText}>Загрузка...</div>
      </Layout>
    );
  }

  if (error || !list) {
    return (
      <Layout title="Ошибка" showBack>
        <div style={{ padding: '24px', color: '#ff3b30', textAlign: 'center' }}>
          {error || 'Список не найден'}
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title={list.my_role === 'admin' ? (
        editingTitle ? (
          <input
            autoFocus
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value.replace(/https?:\/\/\S+/gi, ''))}
            onBlur={handleSaveTitle}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') handleSaveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
            style={{ fontSize: '17px', fontWeight: '600', border: 'none', borderBottom: '2px solid #F05A35', background: 'transparent', outline: 'none', textAlign: 'center', width: '100%', color: '#2D2424' }}
            maxLength={255}
          />
        ) : (
          <span
            onClick={() => { setTitleValue(list.title); setEditingTitle(true); }}
            style={{ cursor: 'text', borderBottom: '1px dashed #C4A99A' }}
            title="Нажми чтобы переименовать"
          >
            {list.title}
          </span>
        )
      ) : list.title}
      showBack onBack={() => navigate('/')} rightAction={rightAction}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Горизонтальные таблетки категорий */}
        <div style={styles.tabsRow}>
          {[...list.categories || []].sort((a, b) => a.position - b.position).map((cat) => (
            <button
              key={cat.id}
              style={{ ...styles.tab, ...(selectedCategory?.id === cat.id && !archiveView ? styles.tabActive : {}), display: 'flex', alignItems: 'center', gap: '6px' }}
              onClick={() => handleCategorySelect(cat)}
            >
              {cat.title}
              {cat.item_count > 0 && (
                <span style={{ fontSize: '11px', opacity: 0.75, fontWeight: '400' }}>
                  {cat.item_count}
                </span>
              )}
              {list.my_role === 'admin' && (
                <>
                  <span
                    onClick={(e) => { e.stopPropagation(); setRenameCategoryTitle(cat.title); setRenameCategoryModal(cat); }}
                    style={{ fontSize: '12px', lineHeight: 1, opacity: 0.6 }}
                  >
                    ✎
                  </span>
                  <span
                    onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat); }}
                    style={{ fontSize: '14px', lineHeight: 1, opacity: 0.6 }}
                  >
                    ×
                  </span>
                </>
              )}
            </button>
          ))}
          {!list.my_category_id && list.my_role !== 'view' && (
            <button style={{ ...styles.tab, color: '#F05A35' }} onClick={handleAddCategory}>
              + категория
            </button>
          )}
          <button
            style={{ ...styles.tab, ...(archiveView ? styles.tabActive : styles.tabArchive) }}
            onClick={archiveView ? () => { setArchiveView(false); setSelectedCategory([...list.categories || []].sort((a, b) => a.position - b.position)[0] || null); } : handleArchiveView}
          >
            📦 Архив
          </button>
        </div>

        {/* Search */}
        {(items.length > 0 || search.trim()) && !archiveView && (
          <div style={styles.searchWrap}>
            <span style={{ position: 'absolute', left: '24px', top: 'calc(50% - 5px)', transform: 'translateY(-50%)', fontSize: '14px', pointerEvents: 'none', color: '#C4A99A' }}>🔍</span>
            <input
              style={styles.searchInput}
              type="text"
              placeholder="Поиск по желаниям..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        )}

        <div style={styles.content}>

          {itemsLoading && (
            <div style={styles.emptyState}>Загрузка...</div>
          )}

          {(() => {
            const q = search.trim().toLowerCase();
            const filteredItems = q
              ? items.filter((i) => {
                  const text = `${i.title || ''} ${i.notes || ''} ${i.url || ''}`.toLowerCase();
                  return q.split(/\s+/).every((word) => text.includes(word));
                })
              : items;

            return (
              <>
                {!itemsLoading && items.length === 0 && selectedCategory && (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>🎁</div>
                    <div>Нет желаний в "{selectedCategory.title}"</div>
                  </div>
                )}

                {!itemsLoading && items.length > 0 && filteredItems.length === 0 && (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>🔍</div>
                    <div>Ничего не найдено</div>
                  </div>
                )}

                {!itemsLoading && !selectedCategory && !archiveView && (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>👈</div>
                    <div>Выбери категорию</div>
                  </div>
                )}

                {!itemsLoading && archiveView && archivedItems.length === 0 && (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>📦</div>
                    <div>Архив пуст</div>
                  </div>
                )}

                {!itemsLoading && archiveView && archivedItems.length > 0 && (
                  <div style={styles.itemsGrid}>
                    {archivedItems.map((item) => (
                      <ItemCard
                        key={item.id}
                        item={item}
                        onUnarchive={handleUnarchive}
                        listId={id}
                        categoryId={item.category_id}
                        canEdit={canEditCategory(item.category_id)}
                      />
                    ))}
                  </div>
                )}

                {!itemsLoading && !archiveView && filteredItems.length > 0 && (
                  <div style={styles.itemsGrid}>
                    {filteredItems.map((item) => (
                      <ItemCard
                        key={item.id}
                        item={item}
                        onArchive={handleArchive}
                        onUnarchive={handleUnarchive}
                        listId={id}
                        categoryId={selectedCategory?.id}
                        canEdit={canEditCategory(selectedCategory?.id)}
                      />
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {selectedCategory && canEditCategory(selectedCategory.id) && !archiveView && (
        <button
          style={styles.fab}
          onClick={() => navigate(`/add?list_id=${id}&category_id=${selectedCategory.id}`)}
        >
          + Добавить
        </button>
      )}

      {shareModal && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 1000 }}
          onClick={() => setShareModal(false)}
        >
          <div
            style={{ backgroundColor: '#FFFFFF', borderRadius: '16px 16px 0 0', padding: '24px 16px', width: '100%', maxHeight: '80vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px' }}>Поделиться</div>

            {selectedCategory && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                {[{ value: 'list', label: '📋 Весь вишлист' }, { value: 'category', label: `📁 ${selectedCategory.title}` }].map((s) => (
                  <button
                    key={s.value}
                    onClick={() => { setShareScope(s.value); setShareLink(''); }}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid var(--tg-theme-hint-color, #ddd)',
                      backgroundColor: shareScope === s.value ? 'var(--tg-theme-button-color, #007aff)' : 'transparent',
                      color: shareScope === s.value ? 'var(--tg-theme-button-text-color, #fff)' : 'var(--tg-theme-text-color, #000)',
                      fontSize: '13px', cursor: 'pointer',
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {shareModalLoading ? (
              <div style={{ textAlign: 'center', padding: '16px', color: 'var(--tg-theme-hint-color, #8e8e93)' }}>Загрузка...</div>
            ) : (
              <>
                {members.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--tg-theme-hint-color, #8e8e93)', textTransform: 'uppercase', marginBottom: '8px' }}>Участники</div>
                    {members.map((m) => (
                      <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--tg-theme-hint-color, #f0f0f0)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '15px' }}>👤</span>
                          <span style={{ fontSize: '14px' }}>{m.first_name || m.username || 'Пользователь'}</span>
                          <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '6px', backgroundColor: 'var(--tg-theme-secondary-bg-color, #f0f0f0)', color: '#2D2424' }}>
                            {m.role === 'admin' ? 'Админ' : m.role === 'edit' ? 'Редактор' : 'Просмотр'}
                          </span>
                        </div>
                        {m.role !== 'admin' && (
                          <button
                            onClick={() => handleRemoveMember(m.id)}
                            style={{ background: 'none', border: 'none', color: '#ff3b30', fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {invites.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--tg-theme-hint-color, #8e8e93)', textTransform: 'uppercase', marginBottom: '8px' }}>Активные ссылки</div>
                    {invites.map((inv) => (
                      <div key={inv.id} style={{ padding: '8px 10px', borderRadius: '10px', backgroundColor: '#F5EDE6', marginBottom: '6px' }}>
                        <div style={{ fontSize: '11px', color: '#8B6B5A', marginBottom: '6px' }}>
                          {inv.role === 'edit' ? '✏️ Редактор' : '👁 Просмотр'} · {inv.category_title ? `📁 ${inv.category_title}` : '📋 Весь вишлист'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', color: '#8B6B5A', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {inv.invite_url}
                          </span>
                          <button
                            onClick={() => {
                              const doCopy = () => { setCopiedInviteId(inv.id); setTimeout(() => setCopiedInviteId(null), 2000); };
                              if (navigator.clipboard) {
                                navigator.clipboard.writeText(inv.invite_url).then(doCopy).catch(() => { const ta = document.createElement('textarea'); ta.value = inv.invite_url; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); doCopy(); });
                              } else {
                                const ta = document.createElement('textarea'); ta.value = inv.invite_url; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); doCopy();
                              }
                            }}
                            style={{ background: 'none', border: 'none', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, color: copiedInviteId === inv.id ? '#34c759' : 'var(--tg-theme-link-color, #007aff)' }}
                          >
                            {copiedInviteId === inv.id ? '✅' : 'Копировать'}
                          </button>
                          <button
                            onClick={() => handleRevokeInvite(inv.id)}
                            style={{ background: 'none', border: 'none', color: '#ff3b30', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                          >
                            Отозвать
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--tg-theme-hint-color, #8e8e93)', textTransform: 'uppercase', marginBottom: '8px' }}>Новая ссылка</div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  {[{ value: 'view', label: '👁 Просмотр' }, { value: 'edit', label: '✏️ Редактор' }].map((r) => (
                    <button
                      key={r.value}
                      onClick={() => { setShareRole(r.value); setShareLink(''); }}
                      style={{
                        flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid var(--tg-theme-hint-color, #ddd)',
                        backgroundColor: shareRole === r.value ? 'var(--tg-theme-button-color, #007aff)' : 'transparent',
                        color: shareRole === r.value ? 'var(--tg-theme-button-text-color, #fff)' : 'var(--tg-theme-text-color, #000)',
                        fontSize: '14px', cursor: 'pointer',
                      }}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>

                {!shareLink ? (
                  <button
                    onClick={handleGenerateLink}
                    disabled={shareLoading}
                    style={{ width: '100%', padding: '13px', backgroundColor: 'var(--tg-theme-button-color, #007aff)', color: 'var(--tg-theme-button-text-color, #fff)', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    {shareLoading ? 'Создаём...' : 'Создать ссылку'}
                  </button>
                ) : (
                  <div>
                    <div style={{ backgroundColor: '#F5EDE6', borderRadius: '10px', padding: '12px', fontSize: '13px', color: '#8B6B5A', wordBreak: 'break-all', marginBottom: '10px' }}>
                      {shareLink}
                    </div>
                    <button
                      onClick={handleCopy}
                      style={{ width: '100%', padding: '13px', backgroundColor: copied ? '#34c759' : 'var(--tg-theme-button-color, #007aff)', color: 'var(--tg-theme-button-text-color, #fff)', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      {copied ? '✅ Скопировано!' : 'Скопировать ссылку'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {renameCategoryModal && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 1000 }}
          onClick={() => setRenameCategoryModal(null)}
        >
          <div
            style={{ backgroundColor: '#FFFFFF', borderRadius: '16px 16px 0 0', padding: '24px 16px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>Переименовать категорию</div>
            <input
              style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid var(--tg-theme-hint-color, #ccc)', fontSize: '15px', marginBottom: '12px', backgroundColor: 'var(--tg-theme-secondary-bg-color, #f5f5f5)', color: 'var(--tg-theme-text-color, #000)', outline: 'none', boxSizing: 'border-box' }}
              type="text"
              value={renameCategoryTitle}
              onChange={(e) => setRenameCategoryTitle(e.target.value.replace(/https?:\/\/\S+/gi, ''))}
              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') handleRenameCategorySubmit(); if (e.key === 'Escape') setRenameCategoryModal(null); }}
              autoFocus
              maxLength={255}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setRenameCategoryModal(null)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid var(--tg-theme-hint-color, #ccc)', backgroundColor: 'transparent', fontSize: '15px', cursor: 'pointer', color: 'var(--tg-theme-text-color, #000)' }}>
                Отмена
              </button>
              <button onClick={handleRenameCategorySubmit} disabled={!renameCategoryTitle.trim()} style={{ flex: 2, padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: 'var(--tg-theme-button-color, #007aff)', color: 'var(--tg-theme-button-text-color, #fff)', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {addCategoryModal && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 1000 }}
          onClick={() => setAddCategoryModal(false)}
        >
          <div
            style={{ backgroundColor: '#FFFFFF', borderRadius: '16px 16px 0 0', padding: '24px 16px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>Новая категория</div>
            <input
              style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid var(--tg-theme-hint-color, #ccc)', fontSize: '15px', marginBottom: '12px', backgroundColor: 'var(--tg-theme-secondary-bg-color, #f5f5f5)', color: 'var(--tg-theme-text-color, #000)', outline: 'none', boxSizing: 'border-box' }}
              type="text"
              placeholder="Название категории"
              value={newCategoryTitle}
              onChange={(e) => setNewCategoryTitle(e.target.value)}
              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') handleAddCategorySubmit(); }}
              autoFocus
              maxLength={255}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setAddCategoryModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid var(--tg-theme-hint-color, #ccc)', backgroundColor: 'transparent', fontSize: '15px', cursor: 'pointer', color: 'var(--tg-theme-text-color, #000)' }}>
                Отмена
              </button>
              <button onClick={handleAddCategorySubmit} disabled={!newCategoryTitle.trim()} style={{ flex: 2, padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: 'var(--tg-theme-button-color, #007aff)', color: 'var(--tg-theme-button-text-color, #fff)', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
                Создать
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
